import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { AccountTree } from '@mui/icons-material';
import { format } from 'date-fns';
import { transactionsApi } from '../services/api';

const TYPE_LABELS = {
  PurchaseOrder: 'PO',
  SalesOrder: 'SO',
  Bill: 'Bill',
  Invoice: 'Inv',
  VendorCredit: 'VC',
  BillPaymentCheck: 'BPChk',
  BillPaymentCreditCard: 'BPCC',
  ReceivePayment: 'RcvPmt',
  ItemReceipt: 'IR',
};

const TYPE_COLORS = {
  PurchaseOrder: '#32578b',
  SalesOrder: '#2e7d32',
  Bill: '#ed6c02',
  Invoice: '#9c27b0',
  VendorCredit: '#d32f2f',
  BillPaymentCheck: '#0277bd',
  BillPaymentCreditCard: '#00838f',
  ReceivePayment: '#558b2f',
  ItemReceipt: '#6a1b9a',
};

// Defines the expected order of transaction types in the chain
const TYPE_ORDER = [
  'PurchaseOrder', 'SalesOrder', 'ItemReceipt',
  'Bill', 'Invoice', 'VendorCredit',
  'BillPaymentCheck', 'BillPaymentCreditCard', 'ReceivePayment',
];

function TransactionChain({ transactionId }) {
  const navigate = useNavigate();
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await transactionsApi.getChain(transactionId);
        if (!cancelled) setChain(data);
      } catch (err) {
        console.error('Error loading chain:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [transactionId]);

  if (loading) {
    return (
      <Paper sx={{ width: '100%', maxWidth: 1000 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="h6">Document Chain</Typography>
        </Box>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (!chain || chain.nodes.length <= 1) return null; // No chain to show

  // Sort nodes by type order, then by date, then by ref number for stable ordering
  // Using ref number as final tiebreaker ensures the same order on every page in the chain
  const sortedNodes = [...chain.nodes].sort((a, b) => {
    const orderA = TYPE_ORDER.indexOf(a.type);
    const orderB = TYPE_ORDER.indexOf(b.type);
    if (orderA !== orderB) return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    if (dateA !== dateB) return dateA - dateB;
    // Stable tiebreaker: ref number (string comparison for numeric-like refs)
    return (a.refNumber || '').localeCompare(b.refNumber || '', undefined, { numeric: true });
  });

  // Deduplicate edges
  const edgeSet = new Set();
  const uniqueEdges = chain.edges.filter(e => {
    const key = `${e.from}-${e.to}`;
    if (edgeSet.has(key)) return false;
    edgeSet.add(key);
    return true;
  });

  return (
    <Paper sx={{ width: '100%', maxWidth: 1000 }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6">
          <AccountTree sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
          Document Chain ({sortedNodes.length})
        </Typography>
      </Box>
      <Box sx={{ position: 'relative' }}>
        {/* Gradient scroll hint on mobile — indicates horizontal scrollability */}
        <Box sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 36,
          background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.9))',
          pointerEvents: 'none',
          zIndex: 1,
        }} />
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        overflowX: 'auto',
        flexWrap: 'nowrap',
        WebkitOverflowScrolling: 'touch',
      }}>
        {sortedNodes.map((node, i) => {
          const isCurrent = node.id === chain.startId;
          const color = TYPE_COLORS[node.type] || '#757575';
          const hasOutgoing = uniqueEdges.some(e => e.from === node.id);
          const label = TYPE_LABELS[node.type] || node.type;

          return (
            <React.Fragment key={node.id}>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" display="block">{label} #{node.refNumber}</Typography>
                    {node.date && <Typography variant="caption" display="block">{format(new Date(node.date), 'MM/dd/yyyy')}</Typography>}
                    {node.amount && <Typography variant="caption" display="block">${parseFloat(node.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>}
                    {node.entityName && <Typography variant="caption" display="block">{node.entityName}</Typography>}
                  </Box>
                }
              >
                <Box
                  component={isCurrent ? 'div' : RouterLink}
                  to={isCurrent ? undefined : `/transaction/${node.id}`}
                  onClick={(e) => {
                    if (isCurrent) return;
                    if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                      e.preventDefault();
                      navigate(`/transaction/${node.id}`);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: isCurrent ? 'default' : 'pointer',
                    minWidth: { xs: 70, sm: 90 },
                    p: 1,
                    borderRadius: 1,
                    border: isCurrent ? `2px solid ${color}` : '1px solid #e0e0e0',
                    backgroundColor: isCurrent ? `${color}10` : 'transparent',
                    '&:hover': !isCurrent ? { backgroundColor: '#f5f5f5' } : {},
                    flexShrink: 0,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <Chip
                    label={label}
                    size="small"
                    sx={{
                      backgroundColor: color,
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                    }}
                  />
                  <Typography variant="caption" fontWeight={isCurrent ? 'bold' : 'medium'} color="primary">
                    #{node.refNumber}
                  </Typography>
                  {node.date && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {format(new Date(node.date), 'MM/dd/yy')}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
              {/* Arrow between nodes */}
              {i < sortedNodes.length - 1 && hasOutgoing && (
                <Typography sx={{ color: 'text.disabled', fontSize: '1.2rem', flexShrink: 0 }}>
                  &rarr;
                </Typography>
              )}
              {i < sortedNodes.length - 1 && !hasOutgoing && (
                <Typography sx={{ color: 'text.disabled', fontSize: '1.2rem', flexShrink: 0 }}>
                  &mdash;
                </Typography>
              )}
            </React.Fragment>
          );
        })}
      </Box>
      </Box>
    </Paper>
  );
}

export default TransactionChain;
